import { ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Button, Card, Checkbox, CheckboxOptionType, Col, Dropdown, Form, Input, InputNumber, Row, Select } from 'antd';
import React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { GUID, ServiceFeeSharingEntityType, SystemAppType } from '@/constants';
import CompanyInput from '@/components/FormItems/CompanyInput';
import { FormInstance } from 'rc-field-form';
import { orderBy } from 'lodash';
import { FeeSharingEntityModel } from '@/__generated';

type Props = {
  availableApps: number[] | null | undefined;
  form: FormInstance<any>;
};
export const parseFeeSharingEntitiesData = (entities: FeeSharingEntityModel[]) => {
  return orderBy(entities, x =>
    ServiceFeeSharingEntityType.__orderedKey.indexOf(ServiceFeeSharingEntityType.__getValue(x.serviceFeeSharingEntityType)),
  ).map(entity => {
    return { ...entity, canDelete: entities.filter(x => x.serviceFeeSharingEntityType === entity.serviceFeeSharingEntityType).length > 1 };
  });
};

export default function FeeSharingEntities({ availableApps, form }: Props) {
  const { t } = useTranslation();

  const feeSharing: number[] = Form.useWatch('feeSharing', form);

  const feeSharingOptions: CheckboxOptionType[] = React.useMemo(() => {
    return ServiceFeeSharingEntityType.__getSelectOptions().filter(op => {
      if (op.value === ServiceFeeSharingEntityType.Mtopo) {
        return true;
      } else if (op.value === ServiceFeeSharingEntityType.Operators) {
        return true;
      } else if (op.value === ServiceFeeSharingEntityType.Merchants) {
        return availableApps?.includes(SystemAppType.ConektoRetail);
      } else if (op.value === ServiceFeeSharingEntityType.Companies) {
        return availableApps?.includes(SystemAppType.ConektoTransport);
      }
      return false;
    });
  }, [availableApps]);

  const feeSharingAddOptions = React.useMemo(() => {
    return feeSharing
      ?.filter(x => x !== ServiceFeeSharingEntityType.Mtopo)
      .map(f => {
        return {
          key: f,
          label: ServiceFeeSharingEntityType.__getValue(f),
        };
      });
  }, [feeSharing]);
  React.useEffect(() => {
    let feeSharingEntities: FeeSharingEntityModel[] = form.getFieldValue('feeSharingEntities') || [];
    if (feeSharingEntities) {
      feeSharingEntities = feeSharingEntities.filter(entity => {
        return feeSharingOptions.some(option => option.value === (entity.serviceFeeSharingEntityType as any));
      });
      form.setFieldValue('feeSharingEntities', parseFeeSharingEntitiesData(feeSharingEntities));
    }
  }, [feeSharingOptions]);

  React.useEffect(() => {
    let feeSharingEntities: FeeSharingEntityModel[] = form.getFieldValue('feeSharingEntities') || [];
    if (feeSharing) {
      feeSharingEntities = feeSharingEntities.filter(x => feeSharing.includes(x.serviceFeeSharingEntityType as any));
      feeSharing.forEach(f => {
        if (!feeSharingEntities.some(x => f === (x.serviceFeeSharingEntityType as any))) {
          feeSharingEntities.push({
            rate: 0,
            serviceFeeSharingEntityType: f as any,
            isPercentageBase: true,
          });
        }
      });
      form.setFieldValue('feeSharingEntities', parseFeeSharingEntitiesData(feeSharingEntities));
    }
  }, [feeSharing]);
  const onAddfeeSharingEntities = (type: any) => {
    let feeSharingEntities: FeeSharingEntityModel[] = form.getFieldValue('feeSharingEntities') || [];
    feeSharingEntities.push({
      isPercentageBase: true,
      rate: 0,
      serviceFeeSharingEntityType: type,
    });
    form.setFieldValue('feeSharingEntities', parseFeeSharingEntitiesData(feeSharingEntities));
  };
  const onDeletefeeSharingEntities = () => {
    let feeSharingEntities: FeeSharingEntityModel[] = form.getFieldValue('feeSharingEntities') || [];
    form.setFieldValue('feeSharingEntities', parseFeeSharingEntitiesData(feeSharingEntities));
  };

  return (
    <Card
      size="small"
      title={<b>{t('servicePages_FeeSharingEntities')}</b>}
      style={{ backgroundColor: '#f5f5f5' }}
      extra={
        feeSharingAddOptions?.length ? (
          <Dropdown
            menu={{
              items: feeSharingAddOptions as any,
              onClick: item => {
                onAddfeeSharingEntities(parseInt(item.key as any));
              },
            }}
            placement="bottomLeft"
          >
            <Button size="small" type="primary" icon={<PlusOutlined />} />
          </Dropdown>
        ) : null
      }
    >
      <Form.Item name="feeSharing">
        <Checkbox.Group options={feeSharingOptions} />
      </Form.Item>
      <Form.List name="feeSharingEntities">
        {(fields, { remove }) => {
          return (
            <div>
              {fields.map((field, index) => {
                return (
                  <Row gutter={ROW_GUTTER} key={field.key}>
                    <Col md={2}>
                      <Form.Item name={[field.name, 'serviceFeeSharingEntityType']} noStyle>
                        <Input type="hidden" />
                      </Form.Item>
                    </Col>
                    <Col md={10}>
                      <Form.Item noStyle dependencies={[['feeSharingEntities', field.name, 'serviceFeeSharingEntityType']]}>
                        {({ getFieldValue }) => {
                          const feeEntitytype = getFieldValue(['feeSharingEntities', field.name, 'serviceFeeSharingEntityType']);

                          if (feeEntitytype === ServiceFeeSharingEntityType.Mtopo) {
                            return (
                              <>
                                <Form.Item name={[field.name, 'entityId']} noStyle>
                                  <Input type="hidden" />
                                </Form.Item>
                                <Form.Item label={t('servicePages_Mtopo')}>
                                  <Input value={'MTOPO'} disabled className="input-value-color" />
                                </Form.Item>
                              </>
                            );
                          } else if (feeEntitytype === ServiceFeeSharingEntityType.Operators) {
                            return (
                              <Form.Item
                                label={t('servicePages_Operators')}
                                name={[field.name, 'entityId']}
                                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                              >
                                <Select>
                                  <Select.Option key={GUID.EMPTY}>{t('ALL')}</Select.Option>
                                </Select>
                              </Form.Item>
                            );
                          } else if (feeEntitytype === ServiceFeeSharingEntityType.Merchants) {
                            return (
                              <Form.Item
                                label={t('servicePages_Merchant')}
                                name={[field.name, 'entityId']}
                                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                              >
                                <Select>
                                  <Select.Option key={GUID.EMPTY}>{t('ALL')}</Select.Option>
                                </Select>
                              </Form.Item>
                            );
                          } else if (feeEntitytype === ServiceFeeSharingEntityType.Companies) {
                            return (
                              <Form.Item
                                label={t('servicePages_TransportCompanies')}
                                name={[field.name, 'entityId']}
                                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                              >
                                <CompanyInput allowSelectAll />
                              </Form.Item>
                            );
                          }
                          return null;
                        }}
                      </Form.Item>
                    </Col>
                    <Col md={4}>
                      <Form.Item name={[field.name, 'rate']} label={t('servicePages_Rate')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <InputNumber min={0} />
                      </Form.Item>
                    </Col>
                    <Col md={7}>
                      <Form.Item label="  " name={[field.name, 'isPercentageBase']} valuePropName="checked">
                        <Checkbox>{t('servicePages_IsPercentageBase')}</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col md={1} style={{ marginTop: 35 }}>
                      <Form.Item noStyle dependencies={[['feeSharingEntities', field.name, 'canDelete']]}>
                        {({ getFieldValue }) => {
                          var canDelete = getFieldValue(['feeSharingEntities', field.name, 'canDelete']);
                          if (canDelete) {
                            return (
                              <Button
                                size="small"
                                onClick={() => {
                                  remove(index);
                                  onDeletefeeSharingEntities();
                                }}
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                              />
                            );
                          } else {
                            return null;
                          }
                        }}
                      </Form.Item>
                    </Col>
                  </Row>
                );
              })}
            </div>
          );
        }}
      </Form.List>
    </Card>
  );
}
